import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAthleteStore } from '@/store';
import { db } from '@/lib/db/db';
import { useLiveQuery } from 'dexie-react-hooks';
import { startOfWeek, isAfter } from 'date-fns';
import { Flame } from 'lucide-react';
import { RecentSessions } from '@/components/dashboard/DashboardComponents';

// New UI System
import { PageShell } from '@/components/ui/PageShell';
import { EliteCard } from '@/components/ui/EliteCard';
import { SectionHeader } from '@/components/ui/SectionHeader';
import { StatTile } from '@/components/ui/StatTile';
import { PrimaryButton, SecondaryButton } from '@/components/ui/Buttons';

export function DashboardPage() {
    const navigate = useNavigate();
    const { athletes, activeId, hydrated } = useAthleteStore();
    const activeAthlete = athletes.find(a => a.id === activeId);

    // Redirect if needed
    useEffect(() => {
        if (!hydrated) return; // Wait for load

        if (athletes.length === 0) {
            navigate('/profiles/create');
            return;
        }

        // If we have athletes but no active one (or invalid one), go to select
        // Checking against actual athlete list validity is safer
        const isValid = athletes.some(a => a.id === activeId);
        if (!activeId || !isValid) {
            navigate('/profiles');
        }
    }, [hydrated, athletes, activeId, navigate]);

    if (!hydrated) return null; // Or a loader

    // 1. Session Data
    const sessions = useLiveQuery(() =>
        db.trainingSessions
            .where('athleteId')
            .equals(activeId)
            .reverse()
            .sortBy('startedAt')
    ) ?? [];

    const recentSessions = sessions.slice(0, 3);

    // 2. Stats Computation
    const weekStart = startOfWeek(new Date(), { weekStartsOn: 1 });
    const weeklyMinutes = sessions
        .filter(s => isAfter(new Date(s.startedAt), weekStart))
        .reduce((sum, s) => sum + Math.round(s.totalSeconds / 60), 0);

    const [streak, setStreak] = useState(0);
    useEffect(() => {
        if (sessions.length === 0) { setStreak(0); return; }
        let count = 0;
        let check = new Date();
        const sessionDates = new Set(sessions.map(s => new Date(s.startedAt).toDateString()));
        // Include today if trained, otherwise check yesterday
        if (!sessionDates.has(check.toDateString())) {
            check = new Date(check.getTime() - 86400000);
        }
        while (sessionDates.has(check.toDateString())) {
            count++;
            check = new Date(check.getTime() - 86400000);
        }
        setStreak(count);
    }, [sessions]);

    // 3. Skill Momentum Data (Temporarily unused in simplified hero dash)
    // const allSkills = useLiveQuery(() => db.skills.toArray()) ?? [];
    // const skillProgress = useLiveQuery(() => db.skillProgress.where('athleteId').equals(activeId).toArray()) ?? [];

    return (
        <PageShell title="Dashboard" subtitle={`Welcome back, ${activeAthlete?.name.split(' ')[0] || 'Wrestler'}`}>
            <div className="space-y-6">
                {/* 1. Hero: Today's Focus */}
                <section>
                    <SectionHeader title="Today's Focus" meta="HERO" className="mb-3" />
                    <EliteCard className="relative aspect-video flex flex-col justify-end p-5 overflow-hidden group cursor-pointer group">
                        {/* Background Image Placeholder */}
                        <div className="absolute inset-0 bg-neutral-800">
                            <div className="w-full h-full bg-gradient-to-br from-neutral-800 to-neutral-900 opacity-50" />
                        </div>
                        {/* Overlay */}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />

                        <div className="relative z-10">
                            <span className="text-[10px] font-bold text-gold-400 uppercase tracking-widest mb-1 block">
                                Fundamentals
                            </span>
                            <h4 className="text-[24px] md:text-[28px] font-black text-white leading-tight uppercase tracking-tight group-hover:text-gold-100 transition-colors">
                                Ankle Pick Setups
                            </h4>
                            <p className="text-text-secondary text-sm font-medium mt-1">Master the tie-up and clear the path.</p>

                            <div className="mt-4">
                                <PrimaryButton className="w-fit" onClick={() => navigate('/train')}>
                                    Enter Session
                                </PrimaryButton>
                            </div>
                        </div>
                    </EliteCard>
                </section>

                {/* 2. Quick Start Presets */}
                <section>
                    <SectionHeader title="Quick Start" meta="TRAINING" className="mb-3" />
                    <div className="grid grid-cols-2 gap-3">
                        <SecondaryButton onClick={() => navigate('/train/quick?type=neutral')}>Neutral Game</SecondaryButton>
                        <SecondaryButton onClick={() => navigate('/train/quick?type=top')}>Top Game</SecondaryButton>
                        <SecondaryButton onClick={() => navigate('/train/quick?type=bottom')}>Bottom Game</SecondaryButton>
                        <SecondaryButton onClick={() => navigate('/train/stance-motion')}>Stance & Motion</SecondaryButton>
                    </div>
                </section>

                {/* 3. This Week Metrics */}
                <section>
                    <SectionHeader
                        title="This Week"
                        meta="METRICS"
                        className="mb-3"
                        action={<span className="text-[10px] text-gold-500 font-bold uppercase tracking-widest cursor-pointer hover:underline" onClick={() => navigate('/analytics')}>View All</span>}
                    />
                    <div className="grid grid-cols-3 gap-3">
                        <StatTile label="Minutes" value={weeklyMinutes} />
                        <StatTile label="Sessions" value={sessions.filter(s => isAfter(new Date(s.startedAt), weekStart)).length} />
                        <StatTile label="Streak" value={streak} accent={streak > 0} icon={Flame} />
                    </div>
                </section>

                {/* 4. Recent Activity */}
                <section>
                    <RecentSessions sessions={recentSessions} />
                </section>
            </div>
        </PageShell>
    );
}
